import React, { useEffect, useState } from 'react';
import { getSeedPhotos, uploadSeedPhoto } from '../../services/seedPhotoService';

const AdminSeedPhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadingTitle, setUploadingTitle] = useState(null);

  const fetchPhotos = async () => {
    try {
      const data = await getSeedPhotos();
      setPhotos(data.photos);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load seed photos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  const handleFileChange = async (title, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingTitle(title);
    setError('');
    try {
      await uploadSeedPhoto(title, file);
      await fetchPhotos();
    } catch (err) {
      setError(err.response?.data?.message || `Failed to update photo for "${title}"`);
    } finally {
      setUploadingTitle(null);
      e.target.value = '';
    }
  };

  if (loading) return <p style={styles.status}>Loading seed photos...</p>;

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>🌱 Default Seed Recipe Photos</h2>
      <p style={styles.subtitle}>
        These photos are automatically applied to the 20 starter recipes whenever a new group's plan activates.
        Replacing one here updates it for every future new signup — it doesn't change photos on recipes that already exist.
      </p>

      {error && <p style={styles.error}>{error}</p>}

      <div style={styles.grid}>
        {photos.map((photo) => (
          <div key={photo.recipe_title} style={styles.card}>
            <div style={styles.imageWrap}>
              {photo.photo_url ? (
                <img src={photo.photo_url} alt={photo.recipe_title} style={styles.image} />
              ) : (
                <div style={styles.placeholder}>🍽️ No photo set</div>
              )}
            </div>
            <p style={styles.recipeTitle}>{photo.recipe_title}</p>
            <label style={styles.uploadBtn}>
              {uploadingTitle === photo.recipe_title ? 'Uploading...' : photo.photo_url ? 'Replace Photo' : 'Set Photo'}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(photo.recipe_title, e)}
                style={{ display: 'none' }}
                disabled={uploadingTitle === photo.recipe_title}
              />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    padding: '32px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  title: { color: '#e67e22', marginBottom: '6px' },
  subtitle: { color: '#888', fontSize: '13px', marginBottom: '24px', lineHeight: 1.5 },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '20px',
  },
  card: {
    border: '1px solid #eee',
    borderRadius: '10px',
    padding: '14px',
    textAlign: 'center',
  },
  imageWrap: {
    width: '100%',
    height: '120px',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#fdf0e3',
    marginBottom: '10px',
  },
  image: { width: '100%', height: '100%', objectFit: 'cover' },
  placeholder: {
    width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#c98a4a', fontSize: '12px',
  },
  recipeTitle: { fontSize: '13px', color: '#333', fontWeight: '600', marginBottom: '10px' },
  uploadBtn: {
    display: 'inline-block',
    padding: '8px 14px',
    backgroundColor: 'white',
    color: '#e67e22',
    border: '1px solid #e67e22',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  status: { textAlign: 'center', color: '#999' },
  error: { color: '#e74c3c', marginBottom: '16px', textAlign: 'center' },
};

export default AdminSeedPhotos;
